import { create } from 'zustand';

export interface UploadMonitorItem {
  id: string;
  fileName: string;
  folderId: string;
  folderName: string;
  status: 'waiting' | 'uploading' | 'done' | 'error';
  percent: number;
  error?: string;
}

export interface ToastAction {
  label: string;
  onClick: () => void | Promise<void>;
}

interface ToastItem {
  id: string;
  title: string;
  body?: string;
  variant: 'info' | 'success' | 'error' | 'warning';
  /** Optional inline action button — used for Undo etc. */
  action?: ToastAction;
  /** Override default 5-second auto-dismiss. Set to 0 for sticky. */
  durationMs?: number;
}

interface UIState {
  /** True when the left sidebar is visible.
   *  Default: open on desktop (>= md), closed on mobile.
   *  Persisted per-user so their last choice survives reloads. */
  sidebarOpen: boolean;
  toasts: ToastItem[];
  /** Count of in-flight file uploads. Used to warn the user before
   *  navigating away or closing the tab. */
  activeUploads: number;
  /** True when the global Cmd+K / Ctrl+K command palette is open. */
  commandPaletteOpen: boolean;
  /** True when the Quick Upload modal is open. Driven from the
   *  command palette so the action can be invoked from anywhere. */
  quickUploadOpen: boolean;
  toggleSidebar: () => void;
  setSidebarOpen: (v: boolean) => void;
  pushToast: (t: Omit<ToastItem, 'id'>) => string;
  dismissToast: (id: string) => void;
  startUpload: () => void;
  endUpload: () => void;
  toggleCommandPalette: () => void;
  setCommandPaletteOpen: (v: boolean) => void;
  setQuickUploadOpen: (v: boolean) => void;
  // Upload monitor
  uploadItems: UploadMonitorItem[];
  uploadMonitorOpen: boolean;
  uploadMonitorCollapsed: boolean;
  addUploadItem: (item: Omit<UploadMonitorItem, 'id'>) => string;
  updateUploadItem: (id: string, patch: Partial<Omit<UploadMonitorItem, 'id'>>) => void;
  clearUploadItems: () => void;
  setUploadMonitorOpen: (v: boolean) => void;
  setUploadMonitorCollapsed: (v: boolean) => void;
}

const SIDEBAR_KEY = 'bb.sidebar_open';

function readSidebar(): boolean {
  try {
    const stored = localStorage.getItem(SIDEBAR_KEY);
    if (stored === '1') return true;
    if (stored === '0') return false;
  } catch {
    /* fall through to default */
  }
  // Default: open on desktop (min-width 768 matches Tailwind md).
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(min-width: 768px)').matches;
  }
  return true;
}

function writeSidebar(v: boolean): void {
  try {
    localStorage.setItem(SIDEBAR_KEY, v ? '1' : '0');
  } catch {
    /* ignore */
  }
}

export const useUIStore = create<UIState>((set, get) => ({
  sidebarOpen: readSidebar(),
  toasts: [],
  activeUploads: 0,
  commandPaletteOpen: false,
  quickUploadOpen: false,
  startUpload: () => set((s) => ({ activeUploads: s.activeUploads + 1 })),
  endUpload: () => set((s) => ({ activeUploads: Math.max(0, s.activeUploads - 1) })),
  toggleSidebar: () => {
    const next = !get().sidebarOpen;
    writeSidebar(next);
    set({ sidebarOpen: next });
  },
  setSidebarOpen: (v) => {
    writeSidebar(v);
    set({ sidebarOpen: v });
  },
  toggleCommandPalette: () =>
    set((s) => ({ commandPaletteOpen: !s.commandPaletteOpen })),
  setCommandPaletteOpen: (v) => set({ commandPaletteOpen: v }),
  setQuickUploadOpen: (v) => set({ quickUploadOpen: v }),
  pushToast: (t) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    set((s) => ({ toasts: [...s.toasts, { ...t, id }] }));
    const duration = t.durationMs ?? 5000;
    if (duration > 0) {
      setTimeout(() => {
        set((s) => ({ toasts: s.toasts.filter((x) => x.id !== id) }));
      }, duration);
    }
    return id;
  },
  dismissToast: (id) => set((s) => ({ toasts: s.toasts.filter((x) => x.id !== id) })),
  // Upload monitor
  uploadItems: [],
  uploadMonitorOpen: false,
  uploadMonitorCollapsed: false,
  addUploadItem: (item) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    set((s) => ({ uploadItems: [...s.uploadItems, { ...item, id }], uploadMonitorOpen: true }));
    return id;
  },
  updateUploadItem: (id, patch) =>
    set((s) => ({ uploadItems: s.uploadItems.map((u) => u.id === id ? { ...u, ...patch } : u) })),
  clearUploadItems: () => set({ uploadItems: [], uploadMonitorOpen: false }),
  setUploadMonitorOpen: (v) => set({ uploadMonitorOpen: v }),
  setUploadMonitorCollapsed: (v) => set({ uploadMonitorCollapsed: v }),
}));
